import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { SystemMetadata } from '../utils/system-metadata.util';

export interface Company {
  id?: string;
  name: string;
  /** Short code for document numbering (e.g. AC). */
  code?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  taxId?: string;
  industry?: string;
  status?: string;
  ownerId?: string | null;
  // GST/Tax Compliance (India) - Optional fields, disabled by default
  gstEnabled?: boolean; // Feature flag, default: false
  gstin?: string; // GST Identification Number (15 characters)
  gstRegistrationType?: 'regular' | 'composition' | 'unregistered'; // default: 'unregistered'
  logoUrl?: string;
  __system?: SystemMetadata;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private api = inject(ApiService);

  async getAll(filters?: {
    search?: string;
    status?: string;
    industry?: string;
  }): Promise<Company[]> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.industry) params.append('industry', filters.industry);
    
    const queryString = params.toString();
    const url = queryString ? `/companies?${queryString}` : '/companies';
    const response = await this.api.get<{ success: boolean; companies: Company[] }>(url);
    return response.companies || [];
  }

  async getById(id: string): Promise<Company | null> {
    const response = await this.api.get<{ success: boolean; company: Company }>(`/companies/${id}`);
    return response.company || null;
  }

  async create(company: Partial<Company>): Promise<{ success: boolean; id?: string }> {
    const response = await this.api.post<{ success: boolean; id: string }>('/companies', company);
    return { success: response.success, id: response.id };
  }

  async update(id: string, company: Partial<Company>): Promise<{ success: boolean }> {
    const response = await this.api.put<{ success: boolean }>(`/companies/${id}`, company);
    return response;
  }

  async delete(id: string): Promise<{ success: boolean }> {
    const response = await this.api.delete<{ success: boolean }>(`/companies/${id}`);
    return response;
  }

  // Member management methods
  async getMembers(companyId: string): Promise<any[]> {
    const response = await this.api.get<{ success: boolean; members: any[] }>(`/companies/${companyId}/members`);
    return response.members || [];
  }

  async addMember(companyId: string, userId: string, role: string): Promise<{ success: boolean }> {
    const response = await this.api.post<{ success: boolean }>(`/companies/${companyId}/members`, { userId, role });
    return response;
  }

  async updateMemberRole(companyId: string, userId: string, role: string): Promise<{ success: boolean }> {
    const response = await this.api.put<{ success: boolean }>(`/companies/${companyId}/members/${userId}`, { role });
    return response;
  }

  async removeMember(companyId: string, userId: string): Promise<{ success: boolean }> {
    const response = await this.api.delete<{ success: boolean }>(`/companies/${companyId}/members/${userId}`);
    return response;
  }

  /**
   * Active company context: membership role + effective enabled module list (for UX gating).
   */
  async getCompanyContext(companyId: string): Promise<{
    success: boolean;
    company: Company;
    role: string | null;
    enabledModules: string[];
  }> {
    return this.api.get(`/companies/${companyId}/context`);
  }

  async inviteMember(
    companyId: string,
    body: {
      email: string;
      name?: string;
      companyRole: string;
      branchId?: string;
      branchRole?: 'manager' | 'staff' | 'member';
    }
  ): Promise<{
    success: boolean;
    existingUser?: boolean;
    inviteId?: string;
    inviteLink?: string;
    message?: string;
  }> {
    return this.api.post(`/companies/${companyId}/invites`, body);
  }
}

